import { useState } from "react";
import clsx from "clsx";

import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import { Select } from "../base/Select";
import SortableList from "./SortableList";
import {
  ALL_TITLE_FIELDS,
  ITitleFieldPriority,
  TITLE_FIELD_LABELS,
  TITLE_FIELD_WEIGHTS,
  TitleField,
  TitleFieldWeight,
  TitleType,
} from "../../models/Category/TitleConfig";

interface FieldPriorityListProps {
  titleType: TitleType;
  priorities: ITitleFieldPriority[];
  onChange: (priorities: ITitleFieldPriority[]) => void;
  onResetToDefault: () => void;
}

const WEIGHT_STYLES: Record<TitleFieldWeight, string> = {
  Mandatory: "bg-green-100 text-green-800",
  High: "bg-emerald-50 text-emerald-700",
  Medium: "bg-amber-50 text-amber-700",
  Low: "bg-gray-100 text-gray-600",
  Excluded: "bg-red-50 text-red-700",
};

export default function FieldPriorityList({
  titleType,
  priorities,
  onChange,
  onResetToDefault,
}: FieldPriorityListProps) {
  const [fieldToAdd, setFieldToAdd] = useState<TitleField | "">("");

  const used = new Set(priorities.map((p) => p.field));
  const available = ALL_TITLE_FIELDS.filter((field) => !used.has(field));

  const setWeight = (field: TitleField, weight: TitleFieldWeight) =>
    onChange(priorities.map((p) => (p.field === field ? { ...p, weight } : p)));

  const removeField = (field: TitleField) =>
    onChange(priorities.filter((p) => p.field !== field));

  const addField = () => {
    if (!fieldToAdd) return;

    onChange([...priorities, { field: fieldToAdd, weight: "Medium" }]);
    setFieldToAdd("");
  };

  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          Title Building Priority ({titleType})
        </h2>
        <Button variant="outlined" size="xs" onClick={onResetToDefault}>
          Reset to Default
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Drag, or use the arrows, to set the order. Top items have highest
        priority.
      </p>

      <SortableList
        items={priorities}
        getKey={(item) => item.field}
        onReorder={onChange}
        emptyText="No fields configured. Add one below."
        renderItem={(item) => (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm truncate">
              {TITLE_FIELD_LABELS[item.field]}
            </span>

            <div className="flex shrink-0 items-center gap-1">
              <span
                className={clsx(
                  "rounded px-1.5 py-0.5 text-[11px]",
                  WEIGHT_STYLES[item.weight],
                )}
              >
                {item.weight}
              </span>

              <Select
                small
                aria-label={`Weight for ${TITLE_FIELD_LABELS[item.field]}`}
                value={item.weight}
                onChange={(e) =>
                  setWeight(item.field, e.currentTarget.value as TitleFieldWeight)
                }
                className="!text-xs !pr-6"
              >
                {TITLE_FIELD_WEIGHTS.map((weight) => (
                  <option key={weight} value={weight}>
                    {weight}
                  </option>
                ))}
              </Select>

              <button
                type="button"
                aria-label={`Remove ${TITLE_FIELD_LABELS[item.field]}`}
                onClick={() => removeField(item.field)}
                className="text-gray-400 hover:text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      />

      {available.length > 0 && (
        <div className="mt-3 flex items-end gap-2">
          <Select
            small
            fullWidth
            label="Add a field"
            value={fieldToAdd}
            onChange={(e) => setFieldToAdd(e.currentTarget.value as TitleField)}
          >
            <option value="">Select a field…</option>
            {available.map((field) => (
              <option key={field} value={field}>
                {TITLE_FIELD_LABELS[field]}
              </option>
            ))}
          </Select>
          <Button size="sm" variant="outlined" onClick={addField} disabled={!fieldToAdd}>
            <span className="flex items-center gap-1">
              <PlusIcon className="h-4 w-4" /> Add
            </span>
          </Button>
        </div>
      )}
    </section>
  );
}
